import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { KeyRound, Star, Quote, Sparkles } from "lucide-react";
import { staffApi } from "@/utils/staffAuth";

// IDLE_MINUTES — set in localStorage `kl_idle_minutes` to enable the
// auto-lock again (e.g. `localStorage.setItem("kl_idle_minutes","15")`).
// Set to 0 (the default) and the lock NEVER fires — Karine asked for
// this on Jun 30 2026: the clinic is too busy to keep re-entering the
// PIN throughout the day. PIPEDA note: the iPad's OS-level screen lock
// (Settings → Display & Brightness → Auto-Lock) still applies and is
// the recommended way to gate physical access between visits.
const IDLE_MINUTES = Number(localStorage.getItem("kl_idle_minutes") ?? 0);
const ROTATE_MS = 8000;

// Featured-treatment slides interleaved between live 5-star reviews.
const FEATURED = [
  { type: "feature", title: "Baby Glow — Redensity 1", line: "Deep hydration & luminous skin quality. From $450." },
  { type: "feature", title: "2 Syringes + FREE Tox", line: "Our most popular filler bundle — $950." },
  { type: "feature", title: "Crown Referral Rewards", line: "Refer a friend — you both earn $50." },
];

/**
 * Front-desk idle lock. After IDLE_MINUTES of no interaction on any
 * /admin or /staff page, the access/refresh JWTs are dropped server-side
 * (kl_trust kept → PIN re-entry) and an elegant full-screen "tap to
 * unlock" overlay appears. Tapping routes to /staff/login?next=<here>,
 * which shows the PIN keypad.
 */
export default function IdleLockScreen() {
  const location = useLocation();
  const [locked, setLocked] = useState(false);
  const [now, setNow] = useState(new Date());
  const [slides, setSlides] = useState(FEATURED);
  const [slideIdx, setSlideIdx] = useState(0);
  const timerRef = useRef(null);
  const lockedRef = useRef(false);

  useEffect(() => {
    // SECURITY UX: IDLE_MINUTES=0 disables the lock entirely (Karine's
    // request — too busy to keep unlocking the iPad). Anything > 0 keeps
    // the original behaviour and operators can re-enable from the
    // browser console with `localStorage.setItem("kl_idle_minutes","15")`.
    if (!IDLE_MINUTES || IDLE_MINUTES <= 0) return;
    const arm = () => {
      if (lockedRef.current) return;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        lockedRef.current = true;
        setLocked(true);
        try {
          await staffApi.post("/auth/lock");
        } catch {
          // Session may already be expired — overlay still shows.
        }
      }, IDLE_MINUTES * 60 * 1000);
    };
    const events = ["pointerdown", "keydown", "touchstart", "wheel"];
    let lastMove = 0;
    const onMove = () => {
      const t = Date.now();
      if (t - lastMove > 5000) {
        lastMove = t;
        arm();
      }
    };
    events.forEach((e) => window.addEventListener(e, arm, { passive: true }));
    window.addEventListener("mousemove", onMove, { passive: true });
    arm();
    return () => {
      clearTimeout(timerRef.current);
      events.forEach((e) => window.removeEventListener(e, arm));
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  // Live clock while locked
  useEffect(() => {
    if (!locked) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [locked]);

  // While locked: fetch live 5-star reviews + admin-managed featured slides,
  // interleave them, then softly rotate every ROTATE_MS.
  useEffect(() => {
    if (!locked) return;
    let cancelled = false;
    (async () => {
      try {
        const [revRes, featRes] = await Promise.allSettled([
          staffApi.get("/testimonials"),
          staffApi.get("/lock-screen/slides"),
        ]);
        if (cancelled) return;
        const revData = revRes.status === "fulfilled" && Array.isArray(revRes.value.data) ? revRes.value.data : [];
        const featData = featRes.status === "fulfilled" && Array.isArray(featRes.value.data) ? featRes.value.data : [];
        const reviews = revData
          .filter((r) => r.rating === 5)
          .slice(0, 6)
          .map((r) => ({ type: "review", text: r.text, name: r.name, service: r.service }));
        const features = featData.length
          ? featData.map((f) => ({ type: "feature", title: f.title, line: f.line }))
          : FEATURED;
        // Interleave: review, feature, review, feature...
        const mixed = [];
        const max = Math.max(reviews.length, features.length);
        for (let i = 0; i < max; i++) {
          if (reviews[i]) mixed.push(reviews[i]);
          if (features[i]) mixed.push(features[i]);
        }
        if (mixed.length) setSlides(mixed);
      } catch {
        // keep FEATURED fallback
      }
    })();
    const id = setInterval(() => setSlideIdx((i) => i + 1), ROTATE_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [locked]);

  if (!locked) return null;

  const unlock = () => {
    const next = encodeURIComponent(location.pathname + location.search);
    window.location.href = `/staff/login?next=${next}`;
  };

  return (
    <div
      className="fixed inset-0 z-[12000] bg-gradient-to-br from-zinc-950 via-zinc-900 to-amber-950/20 flex flex-col items-center justify-center cursor-pointer select-none animate-in fade-in duration-700"
      onClick={unlock}
      onTouchStart={unlock}
      data-testid="idle-lock-screen"
      role="button"
      aria-label="Screen locked. Tap anywhere to unlock"
    >
      <img
        src="/karine-lauzon-logo.png"
        alt="Karine Lauzon RN"
        className="w-28 h-28 object-contain rounded-2xl mb-8 opacity-90"
        draggable={false}
      />
      <div
        className="text-white text-4xl font-light tracking-wide mb-2"
        style={{ fontFamily: "'Playfair Display', serif" }}
        data-testid="idle-lock-clock"
      >
        {now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
      </div>
      <div className="text-zinc-500 text-sm mb-10">
        {now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
      </div>
      {/* Softly rotating featured treatment / 5-star review */}
      {(() => {
        const s = slides[slideIdx % slides.length];
        return (
          <div
            key={slideIdx}
            className="max-w-md w-full px-8 mb-12 text-center animate-in fade-in duration-1000"
            data-testid="idle-lock-slide"
          >
            {s.type === "review" ? (
              <>
                <div className="flex items-center justify-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed italic line-clamp-3">
                  <Quote className="w-3.5 h-3.5 text-amber-500/50 inline mr-1.5 -mt-1" />
                  {s.text}
                </p>
                <p className="text-zinc-500 text-xs mt-3 tracking-wide">
                  — {s.name} · {s.service}
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center gap-1.5 text-amber-400/80 text-[11px] uppercase tracking-[0.25em] mb-3">
                  <Sparkles className="w-3.5 h-3.5" />
                  This Month
                </div>
                <p className="text-white text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {s.title}
                </p>
                <p className="text-zinc-400 text-sm mt-1.5">{s.line}</p>
              </>
            )}
          </div>
        );
      })()}
      <div className="flex items-center gap-2 text-amber-400/90 text-sm tracking-[0.2em] uppercase animate-pulse">
        <KeyRound className="w-4 h-4" />
        Locked — tap to unlock
      </div>
    </div>
  );
}
